import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import { User, UserRole } from '@crowdctrl/core';

/**
 * Service for user management API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get all users for a tenant with optional pagination.
   */
  getUsers(
    tenantId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    users: User[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        users: User[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/users_api/api/tenants/${tenantId}/users`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a single user by ID
   */
  getUser(tenantId: string, userId: string): Observable<{ user: User }> {
    return from(
      this.apiService.get<{ user: User }>(
        this.baseUrl,
        `/users_api/api/tenants/${tenantId}/users/${userId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a new user
   */
  createUser(tenantId: string, user: Partial<User>): Observable<{ user: User }> {
    return from(
      this.apiService.post<{ user: User }, Partial<User>>(
        this.baseUrl,
        `/users_api/api/tenants/${tenantId}/users`,
        user,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update an existing user
   */
  updateUser(tenantId: string, userId: string, user: Partial<User>): Observable<{ user: User }> {
    return from(
      this.apiService.put<{ user: User }, Partial<User>>(
        this.baseUrl,
        `/users_api/api/tenants/${tenantId}/users/${userId}`,
        user,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Delete a user
   */
  deleteUser(tenantId: string, userId: string): Observable<{ success: boolean }> {
    return from(
      this.apiService.delete<{ success: boolean }>(
        this.baseUrl,
        `/users_api/api/tenants/${tenantId}/users/${userId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update user status (suspend/activate)
   */
  updateUserStatus(
    tenantId: string,
    userId: string,
    status: 'active' | 'suspended' | 'deleted',
  ): Observable<{ user: User }> {
    return from(
      this.apiService.put<{ user: User }, { status: 'active' | 'suspended' | 'deleted' }>(
        this.baseUrl,
        `/users_api/api/tenants/${tenantId}/users/${userId}/status`,
        { status },
      ),
    ).pipe(switchMap((obs) => obs));
  }
}
